import { useFormikContext } from 'formik';
import { useCallback, useRef } from 'react';

import { useIsMobileDevice } from '@hivebrite/react-hooks';

import { type QuillEditorV3Ref } from 'hb-react/shared/components/DesignSystem/QuillEditor/types';
import mentionsOptions from 'hb-react/shared/components/DesignSystem/QuillEditor/utils/mentionsOptions';
import QuillEditorWrappedV3 from 'hb-react/shared/components/QuillEditorWrappedV3';

import { type QuillInputProps } from '../types';

export const QuillInput = ({
  ariaLabelledBy,
  containerElement,
  initialEmbed,
  onToggleEmojiPicker,
}: QuillInputProps) => {
  const attachmentsFieldName = 'post[storage_attachments]';
  const embedFieldName = 'post[embed]';
  const id = 'post[content_html]';
  const uploadsFieldName = 'post[storage_uploads]';

  const requiredErrorMessage = __(
    "Text can't be empty. Add a message to publish your post",
  );

  const { isSubmitting, setFieldValue } = useFormikContext();

  const isMobileDevice = useIsMobileDevice();

  const ref = useRef<QuillEditorV3Ref>(null);

  const onAddEmbed = useCallback(
    (embed) => setFieldValue(embedFieldName, embed),
    [setFieldValue],
  );

  /** Set embed inside Quill after a user add a link */
  const onAddLink = useCallback(
    (url: string) => {
      ref.current?.addEmbed(url);
    },
    [ref],
  );

  const onRemoveEmbed = useCallback(
    () => setFieldValue(embedFieldName, {}),
    [setFieldValue],
  );

  const options = {
    embedLy: true,
    toolbar: {
      attachments: true,
      emojiPicker: !isMobileDevice,
      images: true,
      links: true,
      mentions: mentionsOptions,
      video: true,
    },
    toolbarFloats: [
      {
        items: [
          {
            type: 'link',
          },
        ],
        key: 'link',
      },
    ],
  };

  return (
    <QuillEditorWrappedV3
      ariaLabelledBy={ariaLabelledBy}
      attachmentsFieldName={attachmentsFieldName}
      autoFocus
      containerElement={containerElement}
      id={id}
      initialEmbed={initialEmbed}
      isDisabled={isSubmitting}
      isRequired
      minHeight={isMobileDevice ? 148 : 100}
      name={id}
      onAddEmbed={onAddEmbed}
      onAddLink={onAddLink}
      onRemoveEmbed={onRemoveEmbed}
      onToggleEmojiPicker={onToggleEmojiPicker}
      options={options}
      placeholder={__('Write a post')}
      ref={ref}
      requiredErrorMessage={requiredErrorMessage}
      uploadsFieldName={uploadsFieldName}
    />
  );
};
